import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressRemoveClass,
	hasClass,
} from "../../common/html";
import { addWishlistItem, deleteWishlistItem, IWishlistItem } from "../../services/wishlist-service";
import { getCookie, writeCookie } from "../../utils/cookie";
import { createTemp } from "../../services/authentication-service";
import { IDisposable } from "../../utils/disposable";

export interface IWishlistButton {
	readonly dispose: () => void;
	readonly setState: (liked: boolean) => void;
	readonly hasPredefinedProductNameAndPvc: (predefinedProductNameAndPvc: string) => boolean;
}

export interface ICreateWishListButtonOptions {
	readonly onLike?: (el: HTMLButtonElement) => void;
	readonly onDisLike?: (el: HTMLButtonElement) => void;
}
// Creates a wishlist button component
// THis component requires the window.wishlist to be set on the page that initializes it
export function createWishListButton(
	el: HTMLButtonElement,
	opts?: ICreateWishListButtonOptions,
	addEventListeners: boolean = true,
) {
	const iconEl = expressQuerySelector<HTMLElement>(el, ".technical-icon-file", true);
	const iconUseEl = expressQuerySelector<HTMLElement>(iconEl, "use", true);
	const likedClass = "a-wishlist-button--liked";
	const hideClass = "u-hide";
	const likedIconPath = "/svg/heart-filled-symbol.svg#heart-filled-symbol";
	const dislikedIconPath = "/svg/heart-symbol.svg#heart-symbol";
	let executingOnClick = false;
	let disposable: IDisposable | undefined = undefined;
	// The productCategoryCode and predefinedProductNameAndPvc can be changed dynamically (dsp) so we read them on each click
	let productCategoryCode = el.dataset.productcategorycode;
	let predefinedProductNameAndPvc = el.dataset.predefinedproductnameandpvc;

	const onClickAction = () => {
		productCategoryCode = el.dataset.productcategorycode;
		predefinedProductNameAndPvc = el.dataset.predefinedproductnameandpvc;
		if (!productCategoryCode && !predefinedProductNameAndPvc) return;
		const liked = hasClass(el, likedClass);
		if (liked) {
			deleteWishlistItem(productCategoryCode, predefinedProductNameAndPvc).then((_) => {
				expressRemoveClass(el, likedClass);
				iconUseEl.setAttribute("href", dislikedIconPath);
				el.title = el.dataset.liketitle || "";

				if (window.wishlist) {
					const found = window.wishlist.find((item) => {
						return (
							item.productCategoryCode.toLowerCase() === productCategoryCode.toLowerCase() &&
							item.predefinedProductNameAndPvc.toLowerCase() === predefinedProductNameAndPvc.toLowerCase()
						);
					});
					if (found) {
						const index = window.wishlist.indexOf(found);
						if (index >= 0) window.wishlist.splice(index, 1);
					}
				}
				if (opts && opts.onDisLike) opts.onDisLike(el);
			});
		} else {
			addWishlistItem(productCategoryCode, predefinedProductNameAndPvc).then((_) => {
				expressAddClass(el, likedClass);
				iconUseEl.setAttribute("href", likedIconPath);
				el.title = el.dataset.disliketitle || "";
				if (window.wishlist) {
					window.wishlist.push({ productCategoryCode, predefinedProductNameAndPvc } as IWishlistItem);
				}
				if (opts && opts.onLike) opts.onLike(el);
			});
		}
		executingOnClick = false;
	};

	const onClick = (ev: MouseEvent) => {
		ev.preventDefault();
		if (executingOnClick) return;
		executingOnClick = true;
		const accId = getCookie("accid");
		const accType = getCookie("acctype");
		if (!accId) {
			createTemp().then((result) => {
				if (result.success) {
					if (accType !== result.accountType) writeCookie("acctype", result.accountType, 365);
					if (accId !== result.accountId) writeCookie("accid", result.accountId, 365);
					onClickAction();
				}
			});
		} else {
			onClickAction();
		}
	};

	const setState = (liked: boolean) => {
		if (executingOnClick) return;
		executingOnClick = true;
		if (liked) {
			expressAddClass(el, likedClass);
			iconUseEl.setAttribute("href", likedIconPath);
			el.title = el.dataset.disliketitle || "";
		} else {
			expressRemoveClass(el, likedClass);
			iconUseEl.setAttribute("href", dislikedIconPath);
			el.title = el.dataset.liketitle || "";
		}
		executingOnClick = false;
	};

	// If addEventListeners == true (if false, the button is already created and it has a state) we check if the item is already in the wishlist and set the state accordingly
	if (addEventListeners) {
		disposable = expressEventListener(el, "click", onClick);
		// Early exit the element is not hidden (if it is not hidden it is set server side)
		if (!hasClass(el, hideClass)) return;
		// Check if the item is in the wishlist and set the state
		if (window.wishlist && window.wishlist.length > 0) {
			const found = window.wishlist.find((item) => {
				return (
					item.productCategoryCode.toLowerCase() === productCategoryCode.toLowerCase() &&
					item.predefinedProductNameAndPvc.toLowerCase() === predefinedProductNameAndPvc.toLowerCase()
				);
			});
			found ? setState(true) : setState(false);
		}
		expressRemoveClass(el, hideClass);
	}

	return {
		dispose: () => {
			disposable && disposable.dispose();
			expressAddClass(el, hideClass);
		},
		setState,
		hasPredefinedProductNameAndPvc: (predefinedProductNameAndPvc: string) => {
			return el.dataset.predefinedproductnameandpvc.toLowerCase() === predefinedProductNameAndPvc.toLowerCase();
		},
	};
}
